import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Navigation } from "lucide-react";
import ReactMarkdown from "react-markdown";

/** Properties that are always hidden from players */
const HIDDEN_PREFIXES = ["_", "secret", "hidden", "trap", "gm_note", "reveal_condition", "puzzle_solution"];

function isHiddenProperty(key: string): boolean {
  const k = key.toLowerCase();
  return HIDDEN_PREFIXES.some((p) => k.startsWith(p)) || k === "reveal_conditions";
}

/** Nicely format a property key for display */
function formatKey(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Render a property value as readable text */
function renderValue(val: unknown): string {
  if (val == null) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  if (Array.isArray(val)) return val.map((v) => (typeof v === "string" ? v : JSON.stringify(v))).join(", ");
  if (typeof val === "object") {
    return Object.entries(val as Record<string, unknown>)
      .map(([k, v]) => `${formatKey(k)}: ${typeof v === "string" ? v : JSON.stringify(v)}`)
      .join("\n");
  }
  return String(val);
}

export interface LocationDetailEntity {
  id: string;
  name: string;
  description: string;
  image_url: string | null;
  type: string;
  properties: Record<string, unknown>;
}

interface LocationDetailDialogProps {
  entity: LocationDetailEntity | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTravel?: (locationName: string) => void;
  currentLocation?: string;
  showTravelButton?: boolean;
}

export function LocationDetailDialog({ entity, open, onOpenChange, onTravel, currentLocation, showTravelButton }: LocationDetailDialogProps) {
  if (!entity) return null;

  const props = (entity.properties || {}) as Record<string, unknown>;
  const visibleProps = Object.entries(props).filter(
    ([key, val]) => !isHiddenProperty(key) && val != null && val !== "" && val !== false,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto p-0 gap-0">
        {/* Image hero */}
        {entity.image_url && (
          <div className="relative w-full h-44 overflow-hidden rounded-t-lg">
            <img
              src={entity.image_url}
              alt={entity.name}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
          </div>
        )}

        <div className="px-5 pb-5 pt-4 space-y-4">
          <DialogHeader className="space-y-1.5">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-primary shrink-0" />
              <DialogTitle className="text-lg">{entity.name}</DialogTitle>
            </div>
            <Badge variant="outline" className="w-fit text-[10px] capitalize">
              {entity.type}
            </Badge>
          </DialogHeader>

          {/* Description */}
          {entity.description && (
            <div className="prose prose-sm dark:prose-invert max-w-none text-foreground prose-p:my-1.5 prose-p:leading-relaxed">
              <ReactMarkdown>{entity.description}</ReactMarkdown>
            </div>
          )}

          {/* Address */}
          {props.address && typeof props.address === "string" && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/30 rounded-md px-2.5 py-1.5 w-fit">
              <MapPin className="w-3 h-3 text-primary/60 shrink-0" />
              <span>{props.address}</span>
            </div>
          )}

          {/* Known properties */}
          {visibleProps.length > 0 && (
            <div className="space-y-2.5 pt-1">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Known Details
              </h4>
              <div className="space-y-2">
                {visibleProps.map(([key, val]) => {
                  const rendered = renderValue(val);
                  if (!rendered) return null;
                  return (
                    <div key={key} className="rounded-md border border-border/60 bg-muted/30 px-3 py-2">
                      <div className="text-[11px] font-medium text-muted-foreground mb-0.5">
                        {formatKey(key)}
                      </div>
                      <div className="text-sm text-foreground whitespace-pre-wrap">{rendered}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Travel button */}
          {showTravelButton && onTravel && entity.name !== currentLocation && (
            <Button
              className="w-full gap-2 mt-2"
              onClick={() => {
                onTravel(entity.name);
                onOpenChange(false);
              }}
            >
              <Navigation className="w-4 h-4" />
              Travel to {entity.name}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
